/**
 * 通用靜態圖檔解析（從 src/assets/ 取圖，支援任意深度子資料夾）。
 *
 * Vite 在 build 時把 src/assets/ 下所有圖（png/jpg/jpeg/webp/svg/gif）打包成
 * 「相對路徑 → 最終 hashed URL」的 map；runtime 直接查表，無 I/O。
 *
 * 用法（單一 path 字串，斜線分隔任意層級）：
 *   staticImgFromAssets('hbfy/paytable/icon01.png')
 *   staticImgFromAssets('hbfy/history/symbols/wild.png')   // 多層也可以
 *   staticImgFromAssets('exit-avatar.webp')                 // src/assets/ top-level
 *
 * 找不到時 dev mode 印 warn、回傳空字串，component 內 <img :src=""> 自己決定 fallback。
 *
 */
const ASSET_MAP = import.meta.glob<string>('../assets/**/*.{png,jpg,jpeg,webp,svg,gif}', {
  eager: true,
  query: '?url',
  import: 'default',
})

export const staticImgFromAssets = (path: string): string => {
  if (!path) return ''
  const normalized = path.replace(/^\/+/, '')
  const key = `../assets/${normalized}`
  const url = ASSET_MAP[key]

  return url || ''
}

/**
 * @deprecated 舊式：用 md5 相對路徑直接組雲端 URL。改用 `imgByTag`（以後端穩定 tag 引用，
 * 重新上傳同一 tag 不會因 hash path 改變而失效）。僅保留給尚未上雲 / 未匹配 tag 的殘留素材。
 */
export const imgFromCloud = (path: string): string => {
  return `${IMAGES_BASE}/${path}`
}

/**
 * 雲端圖 tag scheme 前綴。assets 存 `imgtag:<tag>`，`resolveImg` 據此查 blobMap（tag→blob/直連 URL）。
 */
export const IMG_TAG_SCHEME = 'imgtag:'

/**
 * 以後端穩定 tag 引用雲端圖（取代 imgFromCloud 的 md5 path）。
 *   - 回傳 sentinel `imgtag:<tag>`，由 `resolveImg` 映射成 blob(簽章模式) / 直連 URL(直連模式)。
 *   - **空 tag → 回 ''**（保留 placeholder 語意：共用 modal / 盤面看到 falsy URL 會跳過渲染）。
 */
export const imgByTag = (tag: string): string => (tag ? `${IMG_TAG_SCHEME}${tag}` : '')

// ============================================================================
// 通用格式化 helper（避免散落各 component）
// ============================================================================

/**
 * 千分位 + 固定 2 位小數，負數用 Unicode minus '−'（U+2212）。
 * 適用：投注金額、盈虧、餘額、win amount 等所有金錢顯示。
 *
 *   formatMoney(1234.5)   → '1,234.50'
 *   formatMoney(-2)       → '−2.00'
 *   formatMoney(0)        → '0.00'
 */
export const formatMoney = (n: number): string => {
  const sign = n < 0 ? '−' : ''
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${sign}${abs}`
}

/**
 * 整數時不加小數位，否則去尾零的小數。給「×N 倍率」這類顯示用。
 *
 *   formatFactor(15)      → '15'
 *   formatFactor(2.5)     → '2.5'
 *   formatFactor(2.50)    → '2.5'
 *   formatFactor(2.05)    → '2.05'
 */
export const formatFactor = (n: number): string => {
  if (!Number.isFinite(n)) return '0'
  if (Number.isInteger(n)) return String(n)
  return n.toFixed(2).replace(/\.?0+$/, '')
}

/** 數字 → 兩位 0-padded 字串（'HH:MM:SS'、'YYYY-MM-DD' 等場景）。 */
export const pad2 = (n: number): string => String(n).padStart(2, '0')

/**
 * 把字串每 N 個字元切成一行（用於顯示長 ID / 交易單號等不適合靠 CSS break-all
 * 的場景，因為 break-all 在英數混雜時會在隨機位置斷、視覺不整齊；JS 固定每
 * N 字切則對齊一致）。
 *
 * @example chunkString('ABCDEFGHIJKLMNOP', 10) → ['ABCDEFGHIJ', 'KLMNOP']
 * @example chunkString('', 10)                 → []
 * @example chunkString(undefined, 10)          → []
 *
 * @param str  來源字串（null / undefined 視為空字串）
 * @param size 每段最大長度（必須 > 0；非法值直接回單一元素或空陣列）
 */
export const chunkString = (str: string | null | undefined, size: number): string[] => {
  const s = str ?? ''
  if (!s) return []
  if (size <= 0) return [s]
  const chunks: string[] = []
  for (let i = 0; i < s.length; i += size) {
    chunks.push(s.slice(i, i + size))
  }
  return chunks
}

// ============================================================================
// Award 相關 helper（HistoryDetail / WinSection / 任何要解析 round.aw 的地方）
// ============================================================================

import type { BackendAward } from '@/api/records/types'
import type { SymbolGridItem } from './useRuleSections'
import { IMAGES_BASE, APP_ENV, AppEnv } from '@/runtimeConfig'

// ============================================================================
// 環境判斷
// ============================================================================

/**
 * 現在是不是 demo 試玩環境（`config.json.env === 'demo'`）。
 *
 * demo 屬部署層級（整包 guest-only）；此判斷等同 runtimeConfig 的 `IS_GUEST_LOGIN`，
 * 提供給非 auth 場景（UI 文案 / 隱藏充值入口 / demo 專屬提示等）就近取用，
 * 免得各處自己 import runtimeConfig 再比字串。
 */
export const isDemoEnv = (): boolean => APP_ENV === AppEnv.Demo

export const AWARD_KEY_MULTIPLIER = 'MULTIPLIER'

/**
 * 延遲派獎結算的虛擬獎 key（ANBS / BSKG 等用此 pattern）。
 *
 * 後端送 `AimAward = "TotalWin"` + `Value = -2` 表示「整 spin 結算後的真實派彩」。
 * 配套：個別普通 award 的 `WinAmount` 都被引擎歸零（個別不出獎、最後一次性結算）。
 *
 * awardKey() 會 uppercase，所以這裡也 uppercase 比對。
 */
export const AWARD_KEY_TOTAL_WIN = 'TOTALWIN'

/**
 * SCATTER / FREE_SPIN 觸發類型 award 的 key 集合（uppercase）。
 * 後端各平台命名不一致，列幾個常見的；遇到新命名直接加進來。
 */
export const SCATTER_AWARD_KEYS = new Set<string>([
  'SCATTER',
  'FREE_SPIN',
  'FREESPIN',
  'FREE_GAME',
  'BONUS',
])

/** 從 award 取 type key（aim_award / aa / a 擇一），uppercase。 */
export const awardKey = (a: BackendAward): string =>
  String(a.aim_award ?? a.aa ?? a.a ?? '').toUpperCase()

/**
 * record 投注額（顯示 / 盈利計算用）：
 * **買免遊（`ibf === true`）用購買價 `ca`**（如 cjgef 75× → 300），否則用 `ba`。
 * `ibf` 但 `ca` 缺失時 fallback `ba`（防後端漏欄位）。
 */
export const recordBetOf = (
  r: { ba?: number | string; ca?: number | string; ibf?: boolean } | null | undefined,
): number => {
  if (!r) return 0
  const raw = r.ibf === true ? (r.ca ?? r.ba) : r.ba
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

/** 將 round.aw（單物件 / 陣列 / null）攤平成陣列。 */
export const awardsOf = (aw: BackendAward[] | BackendAward | null | undefined): BackendAward[] => {
  if (!aw) return []
  return Array.isArray(aw) ? aw : [aw]
}

/** 是不是倍率結算 award（HBFY MULTIPLIER 類型）。 */
export const isMultiplierAward = (a: BackendAward): boolean => awardKey(a) === AWARD_KEY_MULTIPLIER

/** 是不是 scatter / FG 觸發 award。 */
export const isScatterAward = (a: BackendAward): boolean => SCATTER_AWARD_KEYS.has(awardKey(a))

/**
 * 是不是延遲派獎的 TotalWin 虛擬獎（ANBS 等遊戲 spin 結算用）。
 *
 * 出現此 award → 該 spin 走「延遲派獎」流程：個別普通 award 的 WinAmount
 * 都是 0、真實派彩在這隻 award 的 bonus/wa 內。
 */
export const isTotalWinAward = (a: BackendAward): boolean => awardKey(a) === AWARD_KEY_TOTAL_WIN

/**
 * 從 round.aw 派生「中獎格 index 清單」(各 award 的 `pos`/`p`/`positions` 攤平成 number[])。
 *
 * 多款 cascade/ways 遊戲(kldg / tyscs / yyz3258 / hbfy …)的 per-game modal 都用同一段
 * 邏輯派生 winPositions → 收斂成此 util。預設跳過 SCATTER + MULTIPLIER award(不是真正中獎格)。
 *   - skipScatter / skipMultiplier 可關(預設都 true)。
 * 不同遊戲若有特殊規則(如 dypd 宣告帧用 d.ci、jqt 線號、anbs MULTIPLIER 派生)仍各自寫。
 */
export const awardWinPositions = (
  aw: BackendAward[] | BackendAward | null | undefined,
  opts: { skipScatter?: boolean; skipMultiplier?: boolean } = {},
): number[] => {
  const { skipScatter = true, skipMultiplier = true } = opts
  const out: number[] = []
  for (const a of awardsOf(aw)) {
    if (skipScatter && isScatterAward(a)) continue
    if (skipMultiplier && isMultiplierAward(a)) continue
    const positions = a.pos ?? a.p ?? a.positions
    if (!Array.isArray(positions)) continue
    for (const p of positions) {
      const n = Number(p)
      if (Number.isFinite(n)) out.push(n)
    }
  }
  return out
}

/**
 * 從 paytable 反推 award 的「應有 WinAmount」。
 *
 * 用途：延遲派獎遊戲（ANBS 等）的個別 award `WinAmount` 被引擎 zeroed，
 * 直接 read 拿到 0；用此 helper 從 paytableSymbols 查 symbol value、套
 * `bs × bl × value × ways` 公式重算「該 award 沒被 zero 之前的金額」。
 *
 * 非延遲派獎遊戲（HBFY 等）直接 return award 帶的金額（不為 0），不會走重算路線。
 *
 * 順序：
 *   1. award 自帶金額 > 0 → 直接 return（後端權威值優先）
 *   2. 走 paytable lookup：symbol id → payouts[count] → value → 套公式
 *   3. lookup 失敗（無 paytable / 無 symbolId / 無 starCount entry）→ return 0
 *
 * @param a 後端 award 物件
 * @param bs 投注大小 record.bs
 * @param bl 投注倍數 record.bl
 * @param paytableSymbols per-game content.ts 內 paytable.symbols（含 payouts 表）
 */
export const reconstructAwardPayout = (
  a: BackendAward,
  bs: number,
  bl: number,
  paytableSymbols: readonly SymbolGridItem[] | undefined,
): number => {
  const wa = firstNum(a.wa, a.bonus, a.b)
  if (wa > 0) return wa
  if (!paytableSymbols || paytableSymbols.length === 0) return 0

  // 取 award 對應的 symbolId（v / sample / s）
  const rawId = a.v != null ? a.v : (a.sample ?? a.s ?? null)
  if (rawId === null || rawId === undefined || rawId === '') return 0
  const symbolId = Number(rawId)
  if (!Number.isFinite(symbolId)) return 0

  const sym = paytableSymbols.find((s) => s.backendId === symbolId)
  if (!sym?.payouts) return 0

  // 取 n 連數（an / aim_award 字串內擷取數字）
  const starCount = firstNum(a.an) || digitFromString(String(a.aim_award ?? a.aa ?? a.a ?? ''))
  if (!starCount) return 0

  const entry = sym.payouts.find((p) => String(p.count) === String(starCount))
  if (!entry) return 0

  const symbolValue = Number(entry.value)
  if (!Number.isFinite(symbolValue)) return 0

  // ways count 用 award.c — 後端權威值，fallback 到 aim_count / ac 別名。
  const ways = firstNum(a.c, a.aim_count, a.ac) || 1
  return bs * bl * symbolValue * ways
}

/**
 * 從多個候選值中取第一個 finite 數字。
 * 給「後端不同版本字段名不一致」用：
 *   firstNum(a.aim_count, a.ac, a.c)
 *   firstNum(a.bonus, a.b, a.wa)
 */
export const firstNum = (...vals: Array<number | string | undefined | null>): number => {
  for (const v of vals) {
    if (v === undefined || v === null || v === '') continue
    const n = typeof v === 'number' ? v : Number.parseFloat(String(v))
    if (Number.isFinite(n)) return n
  }
  return 0
}

/**
 * 從字串中擷取第一段整數。給「3连」/「3-of-a-Kind」/「4连」等 award type 字串
 * 反推 starCount 用。
 */
export const digitFromString = (s: string | undefined): number => {
  if (!s) return 0
  const m = s.match(/\d+/)
  return m ? Number(m[0]) : 0
}

// ============================================================================
// award 欄位解析共用小工具（useWinSection 各 extractor 重用，純收斂 inline 寫法）
// ============================================================================

/** award 命中格位（pos / p / positions 任一）→ number[]（無則 []）。 */
export const posOf = (a: BackendAward): number[] => {
  const p = a.pos ?? a.p ?? a.positions ?? []
  return Array.isArray(p) ? (p as number[]) : []
}

/** scatter 數量：後端 aim_count / ac / c 優先，否則用命中格數。 */
export const scatterCountOf = (a: BackendAward): number =>
  firstNum(a.aim_count, a.ac, a.c) || posOf(a).length

/** symbolId（v 優先）：a.v != null → String(a.v)，否則 sample / s。 */
export const symbolIdVFirst = (a: BackendAward): string =>
  a.v != null ? String(a.v) : (a.sample ?? a.s ?? '')

/** symbolId（sample 優先）：sample / s，否則 String(a.v)。 */
export const symbolIdSampleFirst = (a: BackendAward): string =>
  a.sample ?? a.s ?? (a.v != null ? String(a.v) : '')

/** starCount（an 優先）：firstNum(a.an) 否則從 aim_award/aa/a 字串擷取數字。 */
export const starCountAnFirst = (a: BackendAward): number =>
  firstNum(a.an) || digitFromString(String(a.aim_award ?? a.aa ?? a.a ?? ''))

/** starCount（a 字串優先）：從 a 擷取數字，否則 firstNum(a.an)。 */
export const starCountAFirst = (a: BackendAward): number =>
  digitFromString(String(a.a ?? '')) || firstNum(a.an)

/** starCount（純從 type 字串）：digitFromString(aim_award / aa / a)；**不**看 a.an。 */
export const starCountFromType = (a: BackendAward): number =>
  digitFromString(String(a.aim_award ?? a.aa ?? a.a ?? ''))

/** wayCount（中奖路）：c / aim_count / ac 任一，最少 1。 */
export const wayCountOf = (a: BackendAward): number => firstNum(a.c, a.aim_count, a.ac) || 1

/** 該 N 連賠付值 wm（way_multi 後備）。 */
export const symbolValueOf = (a: BackendAward): number => firstNum(a.wm, a.way_multi)
